import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Volume2, VolumeX } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAlertPrefs, inHours, popupOn, soundOn, chime, setMuted } from "../lib/alerts";

export const FUN = [
  "Picture abhi baaki hai! Your follow-ups are waiting for their scene.",
  "Tareekh pe tareekh? Not today. Give these leads a next date.",
  "Apna time aayega... right after you call these leads!",
  "Bade bade deals mein chhoti chhoti follow-ups zaroori hain.",
  "How's the josh? Let's put it into a quick follow-up call.",
  "Mogambo khush hoga when these follow-ups are done!",
  "Kitne leads the? Itne! Chalo, start dialling, hero.",
  "Thoda sa follow-up, aur deal ho jayegi superhit!",
  "Zindagi na milegi dobara, but a follow-up today can win the order.",
  "All is well, all is well... once these calls are made!",
];
const STEP_FUN = [
  "Kaam bolta hai! A few steps are waiting for you.",
  "Ek step aur, aur delivery superhit!",
  "Josh high, steps done. Let's close these today.",
  "Hum honge kamyaab... one step at a time.",
];
const KEY = "ax-nudge-snooze";
const pick = (a) => a[Math.floor(Math.random() * a.length)];
export const funLine = () => pick(FUN);
const plural = (n, w) => `${n} ${w}${n > 1 ? "s" : ""}`;

const NudgeCard = ({ id, msg, desc, samples, nav, open, snooze, muted }) => {
  const go = (to) => { toast.dismiss(id); nav(to); };
  return (
    <div className="ax-panel shadow-2xl p-4 w-[min(92vw,380px)] border-l-4 border-l-primary" data-testid="lead-nudge">
      <div className="flex gap-2 items-start"><BellRing className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="min-w-0"><div className="text-sm font-semibold" data-testid="lead-nudge-message">{msg}</div><div className="text-xs text-muted-foreground mt-0.5" data-testid="lead-nudge-desc">{desc}</div></div></div>
      {samples.length > 0 && <div className="mt-2 space-y-0.5">{samples.slice(0, 4).map((s) => (
        <button key={s.key} onClick={() => go(s.to)} className="block w-full text-left text-xs truncate hover:text-primary" data-testid={`lead-nudge-item-${s.key}`}>
          {s.label} <span className="text-muted-foreground">· {s.note}</span></button>))}</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button data-testid="lead-nudge-open" onClick={() => go(open)} className="bg-primary text-primary-foreground h-8 px-3 text-xs">Show me</button>
        <button data-testid="lead-nudge-snooze-15" onClick={() => { snooze(15); toast.dismiss(id); }} className="border h-8 px-3 text-xs">Snooze 15 min</button>
        <button data-testid="lead-nudge-snooze-30" onClick={() => { snooze(30); toast.dismiss(id); }} className="border h-8 px-3 text-xs">Snooze 30 min</button>
        <button data-testid="lead-nudge-sound" onClick={() => setMuted(!muted)} className="border h-8 px-2 text-xs inline-flex items-center gap-1" title={muted ? "Turn sound on" : "Mute sound"}>
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}{muted ? "Sound off" : "Sound on"}</button>
      </div>
    </div>
  );
};

const salesParts = (s, cb) => {
  if (!s && !cb) return { parts: [], samples: [] };
  const parts = [s?.overdue && plural(s.overdue, "follow-up") + " overdue", s?.missing && `${s.missing} without a next date`, cb?.due && plural(cb.due, "callback") + " due",
    s?.tasks && plural(s.tasks, "task") + " given to you"].filter(Boolean);
  const samples = (s?.samples || []).map((l) => ({ key: l.lead_id, label: l.company_name || l.contact_name, note: l.next_contact_date ? `was due ${l.next_contact_date}` : "no next date", to: `/sales/leads/${l.lead_id}` }));
  return { parts, samples };
};

const stepParts = (st, link) => {
  if (!st) return { parts: [], samples: [] };
  const c = st.counts || {};
  const parts = [c.late && plural(c.late, "step") + " late", c.due_today && `${c.due_today} due today`, c.ready && `${c.ready} ready to start`].filter(Boolean);
  const hot = (st.items || []).filter((x) => x.late || x.ready).slice(0, 3);
  return { parts, samples: hot.map((x) => ({ key: x.process_id, label: `${x.uid} ${x.name}`, note: x.late ? "late" : "ready to start", to: `${link}?step=${x.process_id}` })) };
};

const fetchAll = async (prefs, sales) => {
  const on = sales && popupOn(prefs, "lead_nudge");
  const get = (ok, url) => (ok ? api.get(url).then((r) => r.data).catch(() => null) : Promise.resolve(null));
  return Promise.all([get(on, "/sales/nudges"), get(on && popupOn(prefs, "callback"), "/sales/callbacks/count"), get(popupOn(prefs, "ops_reminder"), "/my-steps")]);
};

export const LeadNudger = () => {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const navRef = useRef(nav);
  navRef.current = nav;
  const prefs = useAlertPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const timer = useRef(null);
  const sales = can("sales", "read");
  const every = prefs?.nudge_every || 30;
  const ready = !!user && !!prefs;
  useEffect(() => {
    if (!ready) return undefined;
    const check = async () => {
      const pf = prefsRef.current;
      if (document.body.dataset.lucy === "open" || Number(localStorage.getItem(KEY) || 0) > Date.now() || !inHours(pf)) return;
      const [s, cb, st] = await fetchAll(pf, sales);
      const link = st?.ops ? "/ops/my-tasks" : sales ? "/sales/followups" : "/my-steps";
      const a = salesParts(s, cb);
      const b = stepParts(st, link);
      const parts = [...a.parts, ...b.parts];
      if (!parts.length) return;
      const msg = a.parts.length ? funLine() : pick(STEP_FUN);
      if (soundOn(pf, a.parts.length ? "lead_nudge" : "ops_reminder")) chime();
      if (document.hidden && "Notification" in window && Notification.permission === "granted") new Notification("Axiom One", { body: `${msg}\n${parts.join(" · ")}`, icon: "/axiom-logo.webp" });
      navigator.vibrate?.(200);
      const snooze = (min) => { localStorage.setItem(KEY, String(Date.now() + min * 60000)); clearTimeout(timer.current); timer.current = setTimeout(check, min * 60000 + 1000); };
      toast.custom((id) => <NudgeCard id={id} msg={msg} desc={parts.join(" · ")} samples={[...b.samples, ...a.samples]} open={a.parts.length ? "/sales/followups" : link}
        nav={(to) => navRef.current(to)} snooze={snooze} muted={!!pf.muted} />, { id: "lead-nudge", duration: 120000 });
    };
    const first = setTimeout(check, 4000);
    const t = setInterval(check, every * 60000);
    return () => { clearTimeout(first); clearInterval(t); clearTimeout(timer.current); };
  }, [ready, sales, every]);
  return null;
};
